import { ref, computed, watch } from "vue";
import { fabric } from "fabric";

import { store } from "@/store";

import { canvas, addUserObject } from "@/canvas-api/Canvas";
import { undoActionStack } from "@/canvas-api/ActionStack";

import { isFabricObject } from "@/types";

const currentActiveObject = computed<fabric.Object>(() => store.getters.currentActiveObject);

export const doPaste = ref(false);
export const clonedObject = ref({} as fabric.Object); //Object created when user uses the Copy functionality in TopToolBar

//selection:updated and selection:cleared call this
export const clearClonedObject = () => {
  clonedObject.value = {} as fabric.Object;
};

export const copyActiveObject = () => {
  currentActiveObject.value.clone(function(clone: fabric.Object) {
      clone
        .set({
          left: clone.left! + 10,
          top: clone.top! + 10,
        })
        .setCoords();

      //Triggers Cavnas ACTIVE OBJECT UTILITY Watcher 1
      clonedObject.value = clone;
    },
    ["name", "type", "left", "top", "originX", "originY"]
  );
};

watch([clonedObject, doPaste], (newVals: (fabric.Object | boolean)[]) => {
  const clone = newVals[0] as fabric.Object;
  const paste = newVals[1] as boolean;

  if (isFabricObject(clone) && paste) {
    if (clone.left && clone.top) {
      addUserObject(clone, clone.left + 15, clone.top + 15);
    } else {
      addUserObject(clone);
    }

    copyActiveObject();
    doPaste.value = false;
  }
});

const labelWidth = computed(() => store.getters.labelWidth);
const labelHeight = computed(() => store.getters.labelHeight);
const canvasOrientation = computed(() => store.getters.canvasOrientation);

export const setObjectPosition = (position: string) => {
  const activeObj = canvas.value.getActiveObject() as fabric.Object;
  const { left, top } = canvas.value.getCenter();

  if (activeObj.width && activeObj.height) {
    const original = {
      left: activeObj.left,
      top: activeObj.top,
    };

    undoActionStack.push({
      id: activeObj.id as number,
      type: "positionSet",
      object: fabric.util.object.clone(activeObj),
    });

    switch (position) {
      case "left": {
        if (canvasOrientation.value === "default") {
          activeObj
            .set({
              left: left - labelWidth.value / 2 + activeObj.width / 2,
            })
            .setCoords();
        } else {
          activeObj
            .set({
              left: left - labelWidth.value / 2 + activeObj.height / 2,
            })
            .setCoords();
        }
        break;
      }
      case "right": {
        if (canvasOrientation.value === "default") {
          activeObj
            .set({
              left: left + labelWidth.value / 2 - activeObj.width / 2,
            })
            .setCoords();
        } else {
          activeObj
            .set({
              left: left + labelWidth.value / 2 - activeObj.height / 2,
            })
            .setCoords();
        }
        break;
      }
      case "top": {
        if (canvasOrientation.value === "default") {
          activeObj
            .set({
              top: top - labelHeight.value / 2 + activeObj.height / 2,
            })
            .setCoords();
        } else {
          activeObj
            .set({
              top: top - labelHeight.value / 2 + activeObj.width / 2,
            })
            .setCoords();
        }
        break;
      }
      case "bottom": {
        if (canvasOrientation.value === "default") {
          activeObj
            .set({
              top: top + labelHeight.value / 2 - activeObj.height / 2,
            })
            .setCoords();
        } else {
          activeObj
            .set({
              top: top + labelHeight.value / 2 - activeObj.width / 2,
            })
            .setCoords();
        }
        break;
      }
      case "centerHorizontal": {
        activeObj
          .set({
            left: left,
          })
          .setCoords();
        break;
      }
      case "centerVertical": {
        activeObj
          .set({
            top: top,
          })
          .setCoords();
        break;
      }
      case "centerBoth": {
        activeObj
          .set({
            left: left,
            top: top,
          })
          .setCoords();
        break;
      }
    }

    canvas.value.fire("object:modified", {
      target: activeObj,
      transform: {
        action: "align",
        original,
      },
    });

    canvas.value.renderAll();
  } else {
    throw new Error("Missing properties when setting object alignment, useObjectUtility.ts");
  }
};
